import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Checkbox } from "../components/ui/checkbox";
import { 
  Loader2, CheckCircle2, Clock, Calendar, Target, 
  TrendingUp, ChevronDown, ChevronUp, ArrowRight
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ActionPlanPage() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeaders, loading: authLoading } = useAuth();
  const [plan, setPlan] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);
  const [expandedWeeks, setExpandedWeeks] = useState({ 1: true });

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login?redirect=/action-plan/" + planId);
      return;
    }
    if (planId) {
      fetchPlan();
    }
  }, [user, planId, authLoading]);

  const fetchPlan = async () => {
    try {
      const [planRes, progressRes] = await Promise.all([
        axios.get(`${API}/action-plan/${planId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/action-plan/${planId}/progress`, { headers: getAuthHeaders() })
      ]);
      setPlan(planRes.data);
      setProgress(progressRes.data);
    } catch (error) {
      toast.error("Failed to load action plan");
      navigate("/hub");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTask = async (taskId) => {
    setToggling(taskId);
    try {
      const response = await axios.post(
        `${API}/action-plan/${planId}/task/${taskId}/toggle`,
        {},
        { headers: getAuthHeaders() }
      );
      
      // Update local state
      setPlan(prev => ({
        ...prev,
        tasks: prev.tasks.map(t => 
          t.id === taskId ? { ...t, completed: response.data.completed } : t
        ),
        completed_tasks: response.data.completed_tasks,
        progress_percentage: response.data.progress_percentage
      }));
      
      if (response.data.completed) {
        toast.success("Task completed! Keep going!");
      }
    } catch (error) {
      toast.error("Failed to update task");
    } finally {
      setToggling(null);
    }
  };

  const toggleWeek = (weekNum) => {
    setExpandedWeeks(prev => ({ ...prev, [weekNum]: !prev[weekNum] }));
  };

  const getTasksByWeek = (tasks) => {
    const weeks = { 1: [], 2: [], 3: [], 4: [], 5: [] };
    tasks.forEach(task => {
      const week = Math.min(Math.ceil(task.day / 7), 5);
      weeks[week].push(task);
    });
    return weeks;
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground mb-4">Action plan not found</p>
        <Link to="/hub">
          <Button>Go to Validation Hub</Button>
        </Link>
      </div>
    );
  }

  const tasksByWeek = getTasksByWeek(plan.tasks);
  const completedTasks = plan.completed_tasks || 0;
  const totalTasks = plan.total_tasks || plan.tasks.length;
  const progressPercentage = plan.progress_percentage || 0;

  return (
    <div className="py-8 md:py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl md:text-3xl font-bold mb-2">30-Day Action Plan</h1>
          <p className="text-muted-foreground">
            Complete these tasks to validate your business idea
          </p>
        </div>

        {/* Progress Overview */}
        <Card className="mb-8" data-testid="progress-card">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-2xl font-bold text-primary">{progressPercentage}%</span>
                </div>
                <div>
                  <h3 className="font-semibold">Overall Progress</h3>
                  <p className="text-sm text-muted-foreground">
                    {completedTasks} of {totalTasks} tasks completed
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>Day {progress?.days_elapsed || 1} of 30</span>
                </div>
                {progress?.on_track ? (
                  <span className="flex items-center gap-1 text-emerald-600">
                    <TrendingUp className="w-4 h-4" />
                    On track
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-amber-600">
                    <Clock className="w-4 h-4" />
                    Catch up needed
                  </span>
                )}
              </div>
            </div>
            <Progress value={progressPercentage} className="h-3" />
          </CardContent>
        </Card>

        {/* Tasks by Week */}
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map(weekNum => {
            const weekTasks = tasksByWeek[weekNum] || [];
            if (weekTasks.length === 0) return null;
            
            const weekCompleted = weekTasks.filter(t => t.completed).length;
            const weekProgress = Math.round((weekCompleted / weekTasks.length) * 100);
            const isExpanded = expandedWeeks[weekNum];
            
            return (
              <Card key={weekNum} data-testid={`week-${weekNum}-card`}>
                <button
                  onClick={() => toggleWeek(weekNum)}
                  className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      weekProgress === 100 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : weekProgress > 0 
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      {weekProgress === 100 ? (
                        <CheckCircle2 className="w-5 h-5" />
                      ) : (
                        <span className="font-semibold">{weekNum}</span>
                      )}
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold">Week {weekNum}</h3>
                      <p className="text-xs text-muted-foreground">
                        {weekCompleted}/{weekTasks.length} tasks • {weekProgress}% complete
                      </p>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-muted-foreground" />
                  )}
                </button>
                
                {isExpanded && (
                  <CardContent className="pt-0 pb-4">
                    <div className="space-y-3">
                      {weekTasks.map(task => (
                        <div 
                          key={task.id}
                          className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                            task.completed 
                              ? 'bg-emerald-50 border-emerald-200' 
                              : 'hover:bg-muted/50'
                          }`}
                          data-testid={`task-${task.id}`}
                        >
                          <Checkbox
                            id={`task-${task.id}`}
                            checked={task.completed}
                            onCheckedChange={() => handleToggleTask(task.id)}
                            disabled={toggling === task.id}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <label 
                              htmlFor={`task-${task.id}`}
                              className={`text-sm cursor-pointer ${
                                task.completed ? 'line-through text-muted-foreground' : ''
                              }`}
                            >
                              <span className="font-medium text-xs text-primary mr-2">
                                Day {task.day}
                              </span>
                              {task.task}
                            </label>
                            {task.category && (
                              <span className="block text-xs text-muted-foreground mt-1">
                                {task.category}
                              </span>
                            )}
                          </div>
                          {toggling === task.id && (
                            <Loader2 className="w-4 h-4 animate-spin text-primary" />
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>

        {/* Day 30 CTA */}
        {progress?.days_elapsed >= 25 && (
          <Card className="mt-8 border-primary/20 bg-primary/5" data-testid="day30-cta">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Target className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold mb-1">Ready to Retake Your Assessment?</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    You're approaching Day 30! Retake the Full Assessment to see how much your FoundrScore has improved.
                  </p>
                  <Link to="/assessment">
                    <Button className="rounded-full">
                      Retake Assessment
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bottom navigation */}
        <div className="mt-8 flex justify-center gap-4">
          <Link to="/hub">
            <Button variant="outline" className="rounded-full">
              Back to Validation Hub
            </Button>
          </Link>
          <Link to="/score-history">
            <Button variant="outline" className="rounded-full">
              View Score History
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
